/**
 * CreditsButton — Header pill that shows current credits and opens the
 * unified CreditsPanel modal on click.
 *
 * Reused by: Header, Sidebar, Profile, Chat "Recargar créditos" CTA, etc.
 * Every credits entry point should route through `useCredits().openCreditsPanel`.
 */
import React from 'react';
import { Coins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCredits } from '../../contexts/CreditsContext';
import { useLocale } from '../../contexts/LocaleContext';

type Props = {
  variant?: 'pill' | 'compact' | 'primary';
  testId?: string;
  returnTo?: string;
  className?: string;
};

/**
 * CreditsButton — Header pill that shows current credits and navigates
 * to the dedicated /creditos page (Sprint v1.11 — no more bottom-sheet
 * modal; the purchase flow lives on the /creditos page and uses a
 * CENTERED PurchaseModal overlay defined in Creditos.tsx).
 *
 * The `returnTo` prop is passed as a query param so /creditos can send
 * the user back to where they came from after the checkout completes.
 */
export const CreditsButton: React.FC<Props> = ({
  variant = 'pill',
  testId = 'credits-button-header',
  returnTo,
  className,
}) => {
  const { balance } = useCredits();
  const { locale, t } = useLocale();
  const nav = useNavigate();
  // Sprint P0 Quirúrgico Feb 2026 · muestra el saldo real INCLUYENDO
  // negativo (sobregiro hasta -7). Antes se clampeaba a 0 en el
  // CreditsContext. Ahora el número negativo se renderiza tal cual
  // ("-12", "-42") para que el usuario sepa cuánto debe.
  const localeTag = locale === 'en' ? 'en-US' : 'es-ES';
  const display = balance === null ? '···' : balance.toLocaleString(localeTag);

  const base = 'inline-flex items-center gap-2 font-medium tracking-wide transition-all';
  const styles = {
    pill:
      'rounded-full border border-cyan-400/40 bg-slate-950/60 backdrop-blur px-4 py-1.5 text-sm text-cyan-100 hover:border-cyan-300 hover:bg-cyan-500/10',
    compact:
      'rounded-lg px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800/60',
    primary:
      'rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950 px-5 py-2.5 text-sm font-semibold hover:from-cyan-300 hover:to-cyan-200 shadow-[0_10px_25px_-10px_rgba(34,211,238,0.6)]',
  }[variant];

  const goToCredits = () => {
    const qs = returnTo ? `?back=${encodeURIComponent(returnTo)}` : '';
    nav(`/creditos${qs}`);
  };

  return (
    <button
      type="button"
      onClick={goToCredits}
      data-testid={testId}
      className={[base, styles, className || ''].join(' ')}
    >
      <Coins size={14} className={variant === 'primary' ? 'text-slate-950' : 'text-cyan-300'} />
      {variant === 'primary' ? (
        <>{t('credits.refill')}</>
      ) : (
        <>
          <span
            className="text-slate-400"
            style={balance !== null && balance < 0 ? { color: 'hsl(0 84% 65%)' } : undefined}
            data-testid="credits-header-label"
          >
            {t('credits.label')}
          </span>
          <span
            className="font-semibold"
            data-testid="credits-header-value"
            style={balance !== null && balance < 0 ? { color: 'hsl(0 84% 65%)' } : undefined}
          >
            {display}
          </span>
        </>
      )}
    </button>
  );
};

export default CreditsButton;
